/*
    Represents all of the data diffs between build and live for a given interaction set
    needed for commit page

    Commit Data contains corresponding diffs for:
       interactions: Array<Interaction>;
       interaction_nodes: Array<InteractionNode>;
       interval_sets: Array<IntervalSet>;
       intervals: Array<Interval>;
       communication_sets: Array<CommunicationSet>;

    Can use internal and static functions to populate commit data + diffs into instance,
    and also return the data separately if needed
*/

import CommunicationSet from "../../communications/CommunicationSet";
import Interval from "../../communications/Interval";
import IntervalSet from "../../communications/IntervalSet";
import Interaction from "../Interaction";
import InteractionNode from "../InteractionNode";
import store from "../../../store/Store";
import { navSliceActions } from "../../../store/slices/NavSlice";
import { enqueueSnackbar } from "notistack";
import InteractionChange from "./InteractionChange";

const testing = process.env.REACT_APP_TEST_MODE === "test";

export interface CommitDataFetchProps {
  toolTipLabel: string | null;
  allInteractionsForThisSet: Array<Interaction>;
  interactionsThatChanged: Array<Interaction>;
  numChildInteractionDiffs: number;
  intervalSetsForThisCommitList: Array<IntervalSet>;
  intervalsForThisCommitList: Array<Interval>;
  commSetsForThisCommitList: Array<CommunicationSet>;
}

class CommitData {
  public interactions: Array<Interaction>;
  public interaction_nodes: Array<InteractionNode>;
  public interval_sets: Array<IntervalSet>;
  public intervals: Array<Interval>;
  public communication_sets: Array<CommunicationSet>;
  public setID: number | null | undefined;
  public parentID: number | null | undefined;
  public shared: boolean;
  public commitFetchData?: CommitDataFetchProps


  constructor(
    interactions: Array<Interaction>,
    interaction_nodes: Array<InteractionNode>,
    interval_sets: Array<IntervalSet>,
    intervals: Array<Interval>,
    communication_sets: Array<CommunicationSet>,
    setID: number | null | undefined,
    parentID: number | null | undefined,
    shared: boolean,
    commitFetchData?: CommitDataFetchProps
  ) {
    this.setID = setID;
    this.interactions = interactions;
    this.interaction_nodes = interaction_nodes;
    this.interval_sets = interval_sets;
    this.intervals = intervals;
    this.communication_sets = communication_sets;
    this.parentID = parentID;
    this.shared = shared;
    this.commitFetchData = commitFetchData;

  }

  public commit = async (
    interactionsToCommitOmit: Array<number>,
    intervalSetsToCommitOmit: Array<number>,
    intervalsToCommitOmit: Array<number>,
    communicationsToCommitOmit: Array<number>) => {
    // if no parent or set is passed in,
    // we are assuming this is the root/global commit screen
    // in this case we want to call func_commit_interaction_set
    // for each interaction in the interactions array
    if (!this.setID && !this.parentID) {
      this.interactions.forEach((interaction) => {
        const data = {
          set_interaction_id: interaction.id, unselected_ids: {
            interval: intervalsToCommitOmit,
            condition: [],
            interaction: interactionsToCommitOmit,
            interval_set: intervalSetsToCommitOmit,
            communication: [],
            interaction_node: [],
            communication_set: communicationsToCommitOmit,
            interaction_node_interval: [],
            interaction_instance_property: []
          }
        };
        const url: string =
          process.env.REACT_APP_API_URL + "/rpc/func_commit_interaction_set";
        fetch(url, {
          method: "POST",
          body: JSON.stringify(data),
          headers: {
            Authorization: "Bearer " + process.env.REACT_APP_POSTGREST_JWT,
            "Content-Type": "application/json",
            "Content-Profile": testing ? "test" : "build"
          },
        }).then(async (data: any) => {
          store.dispatch(navSliceActions.closeRightDrawer({}));
        });
      });
    } else {
      // if there is a parent and set present, we just want to commit that particular set
      const data = {
        set_interaction_id: this.setID, unselected_ids: {
          interval: intervalsToCommitOmit,
          condition: [],
          interaction: interactionsToCommitOmit,
          interval_set: intervalSetsToCommitOmit,
          communication: [],
          interaction_node: [],
          communication_set: communicationsToCommitOmit,
          interaction_node_interval: [],
          interaction_instance_property: []
        }
      };
      const url: string =
        process.env.REACT_APP_API_URL + "/rpc/func_commit_interaction_set";

      fetch(url, {
        method: "POST",
        body: JSON.stringify(data),
        headers: {
          Authorization: "Bearer " + process.env.REACT_APP_POSTGREST_JWT,
          "Content-Type": "application/json",
          "Content-Profile": testing ? "test" : "build"
        },
      }).then(async (data: any) => {
        store.dispatch(navSliceActions.closeRightDrawer({}));
      });
    }

    enqueueSnackbar("The selected interactions have been deployed and are now live!", { variant: "success" });
    store.dispatch(navSliceActions.closeRightDrawer({}));

  };

  // use the object props to get the commit data based on shared, parent, and set
  public getDiff = async (): Promise<CommitDataFetchProps> => {
    let allInteractionsForThisSet: any = [];
    let interactionsThatChanged: any = [];
    let numChildInteractionDiffs = 0;
    let toolTipLabel = "";
    // for communications
    let commData: any = {};

    const idToSearch = this.shared && this.parentID ? this.parentID : this.setID;

    if (idToSearch === null) {
      return {
        toolTipLabel: "",
        allInteractionsForThisSet: [],
        interactionsThatChanged: [],
        numChildInteractionDiffs: 0,
        intervalSetsForThisCommitList: [],
        intervalsForThisCommitList: [],
        commSetsForThisCommitList: []
      } as CommitDataFetchProps
    }

    // get all interactions/nodes for this set
    const route =
      "/interaction_node?select=*,joined_interaction:interaction!fk_interaction_node_child(*)&set_interaction_id=eq." +
      idToSearch;
    const url: string = process.env.REACT_APP_API_URL + route;

    try {
      let response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: "Bearer " + process.env.REACT_APP_POSTGREST_JWT,
          "Content-Type": "application/json",
          "Accept-Profile": testing ? "test" : "build",
        },
      });
      let data = await response.json();

      if (!Array.isArray(data)) {
        throw new Error("Expected an array for interaction nodes");
      }

      this.interaction_nodes = data;

      allInteractionsForThisSet = data.map((interactionNode: any) => {
        return interactionNode.joined_interaction;
      });

      this.interactions = allInteractionsForThisSet;

      const routeIsAlreadyLive =
        "/interaction_set_update?interaction_set=eq." + idToSearch;
      const urlIsAlreadyLive: string =
        process.env.REACT_APP_API_URL + routeIsAlreadyLive;

      response = await fetch(urlIsAlreadyLive, {
        method: "GET",
        headers: {
          Authorization: "Bearer " + process.env.REACT_APP_POSTGREST_JWT,
          "Content-Type": "application/json",
          "Accept-Profile": testing ? "test" : "build",
        },
      });
      data = await response.json();

      if (!Array.isArray(data)) {
        throw new Error("Expected an array for interaction set updates");
      }

      const interactionID = data;

      if (!interactionID || interactionID.length < 1) {

        numChildInteractionDiffs = allInteractionsForThisSet.length;
        toolTipLabel =
          "There are currently " +
          interactionsThatChanged.length +
          " interactions that are ahead of production. Click to commit.";

        interactionsThatChanged = [...allInteractionsForThisSet];

        commData = await CommitData.getCommunicationsDataForInteractions(
          allInteractionsForThisSet
        ); // Ensure this is awaited

      } else {
        const route = "/modified_since_last_published";
        const url: string = process.env.REACT_APP_API_URL + route;

        const response = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: "Bearer " + process.env.REACT_APP_POSTGREST_JWT,
            "Content-Type": "application/json",
            "Accept-Profile": testing ? "test" : "build",
          },
        });
        const data = await response.json();

        if (!Array.isArray(data)) {
          throw new Error("Expected an array for modified since last published records");
        }

        const modifiedSinceLastPublishedRecords = data;

        const thisSetID = this.shared ? this.parentID : this.setID;
        const interactionsForThisCommitList =
          modifiedSinceLastPublishedRecords.reduce(function (
            filtered: Array<Interaction>,
            modifiedSinceLastPublishedRecord: any
          ) {
            if (
              thisSetID ==
              modifiedSinceLastPublishedRecord.set_interaction_id
            ) {
              const thisInteractionIndex = allInteractionsForThisSet.findIndex(
                (interaction: Interaction) => {
                  return (
                    interaction.id ===
                    modifiedSinceLastPublishedRecord.child_interaction_id
                  );
                }
              );
              const thisInteraction = allInteractionsForThisSet[thisInteractionIndex];
              const newInteraction = new Interaction(
                modifiedSinceLastPublishedRecord.child_interaction_id,
                modifiedSinceLastPublishedRecord.title,
                thisInteraction.shared,
                thisInteraction.properties,
                ""
              );
              filtered.push(newInteraction);
            }
            return filtered;
          },
            []);

        numChildInteractionDiffs = interactionsForThisCommitList.length;
        interactionsThatChanged = [...interactionsForThisCommitList];
        toolTipLabel =
          "There are currently " +
          numChildInteractionDiffs +
          " interactions that are ahead of production. Click to commit.";

        commData = await CommitData.getCommunicationsDataForInteractions(
          interactionsForThisCommitList
        ); // Ensure this is awaited
      }

      this.intervals = commData.intervalsForThisCommitList
      this.interval_sets = commData.intervalSetsForThisCommitList
      this.communication_sets = commData.commSetsForThisCommitList

      this.commitFetchData = {
        ...commData,
        toolTipLabel,
        allInteractionsForThisSet,
        interactionsThatChanged,
        numChildInteractionDiffs,
      } as CommitDataFetchProps;
      return this.commitFetchData;
    } catch (error) {
      console.error("Error fetching data:", error);
      throw error;
    }
  };


  //given a given array of interactions, get the corresponding interval_sets, intervals, and communication_sets
  private static getCommunicationsDataForInteractions = async (interactionsForThisCommitList: Array<Interaction>) => {
    const intervalSetsForThisCommitList: any = [];
    const intervalsForThisCommitList: any = [];
    const commSetsForThisCommitList: any = [];

    for (const interactionForThisCommit of interactionsForThisCommitList) {

      const intervalSetID = interactionForThisCommit.properties.intervalSetID || 0;
      const route = "/interval_set?id=eq." + intervalSetID;
      const url: string = process.env.REACT_APP_API_URL + route;

      // if there are no interval sets here, theres nothing more to do
      if (interactionForThisCommit.properties.type == "interval_set" && intervalSetID != 0) {
        // interval_sets
        await fetch(url, {
          method: "GET",
          headers: {
            Authorization: "Bearer " + process.env.REACT_APP_POSTGREST_JWT,
            "Content-Type": "application/json",
            "Accept-Profile": testing ? "test" : "build",
          },
        }).then(async (intervalSetData: any) => {
          intervalSetData = await intervalSetData.json()
          intervalSetsForThisCommitList.push(...intervalSetData);
          for (const intervalSetForThisCommit of intervalSetData) {

            const intervalSetID = intervalSetForThisCommit.id
            const route = "/interval?interval_set_id=eq." + intervalSetID;
            const url: string = process.env.REACT_APP_API_URL + route;

            // intervals
            await fetch(url, {
              method: "GET",
              headers: {
                Authorization: "Bearer " + process.env.REACT_APP_POSTGREST_JWT,
                "Content-Type": "application/json",
                "Accept-Profile": testing ? "test" : "build",
              },
            }).then(async (intervalData: any) => {
              intervalData = await intervalData.json()
              intervalsForThisCommitList.push(...intervalData);

              for (const intervalForThisCommit of intervalData) {

                const commSetID = intervalForThisCommit.communication_set_id || 0;
                const route = "/communication_set?id=eq." + commSetID;
                const url: string = process.env.REACT_APP_API_URL + route;
                // comm sets
                await fetch(url, {
                  method: "GET",
                  headers: {
                    Authorization: "Bearer " + process.env.REACT_APP_POSTGREST_JWT,
                    "Content-Type": "application/json",
                    "Accept-Profile": testing ? "test" : "build",
                  },
                }).then(async (commSetData: any) => {
                  commSetData = await commSetData.json()
                  commSetsForThisCommitList.push(...commSetData);
                  return commSetData;

                });
              }
            });
          }
        });
      }
    }
    return {
      intervalSetsForThisCommitList,
      intervalsForThisCommitList,
      commSetsForThisCommitList,
    };
  }


}

export default CommitData;


